import React, { useMemo } from 'react';
import { useTable, usePagination, useGlobalFilter, useFilters } from 'react-table';
import { GlobalFilter } from '../globalFilter';
import { SelectColumnFilter, multiSelectFilter } from './dashBoard/SelectColumnFilter1';
import { ColumnToggle } from './ColumnToggle';
import { getBoxInfo } from '../../utils/boxInfo';

const dash = (v) => (v === null || v === undefined || v === "") ? "—" : v;

/**
 * ConvertedItems — displays only conversion-type adjustments
 * Receives adjustments from parent StockAdjustment — no Redux dispatch here.
 *
 * @param {array}  adjustments — full adjustments array from parent
 * @param {string} unitLabel   — helper passed from parent
 */
export const ConvertedItems = ({ adjustments = [] }) => {

  // ── Filter only conversion records ────────────────────────
  const conversions = useMemo(() =>
    adjustments.filter(a => a.type === "conversion"),
  [adjustments]);

  const columns = useMemo(() => [
    {
      Header: 'No',
      id: 'no',
      Cell: ({ row }) => row.index + 1,
    },
    {
      Header: 'Date',
      accessor: 'adjustmentDate',
      Filter: SelectColumnFilter, filter: 'multiSelectFilter',
      Cell: ({ value }) => value
        ? new Date(value).toLocaleDateString() : "—",
    },
    {
      Header: 'Source Product',
      accessor: 'product.name',
      Filter: SelectColumnFilter, filter: 'multiSelectFilter',
      Cell: ({ value }) => dash(value),
    },
    {
      Header: 'Source SKU',
      accessor: 'product.sku',
      Cell: ({ value }) => dash(value),
    },
    {
      Header: 'Source Qty',
      accessor: 'quantity',
      Cell: ({ value, row }) => {
        const unit = row.original.product?.category?.measurementUnit || "pcs";
        const label = unit === "m2" ? "m²"
          : unit === "linear_meter" ? "m" : "pcs";
        return `${value.toFixed(3)} ${label}`;
      },
    },
    {
      Header: 'Source Batch Stock',
      accessor: 'productBatch.remainingQuantity',
      Cell: ({ value, row }) => {
        const deducted  = row.original.quantity || 0;
        const remaining = value || 0;
        return `${remaining.toFixed(3)} (was ${ (remaining + deducted).toFixed(3) }).`;
      },
    },
    {
      Header: '→ Target Product',
      accessor: 'convertedTo.product.name',
      Filter: SelectColumnFilter, filter: 'multiSelectFilter',
      Cell: ({ value }) => value
        ? <span className="text-success fw-semibold">{value}</span>
        : <span className="text-muted">—</span>,
    },
    {
      Header: 'Target SKU',
      accessor: 'convertedTo.product.sku',
      Cell: ({ value }) => dash(value),
    },
    {
      Header: 'Target Qty',
      id: 'targetQty',
      accessor: row => row.convertedTo,
      Cell: ({ value }) => {
        if (!value?.quantity) return <span className="text-muted">—</span>;
        const unit = value.product?.category?.measurementUnit || "pcs";
        const label = unit === "m2" ? "m²"
          : unit === "linear_meter" ? "m" : "pcs";

        // Box info for target quantity
        const boxInfo = value.product?.itemsPerBox
          ? getBoxInfo(value.quantity, {
              ...value.product,
              category: typeof value.product.category === "object"
                ? value.product.category
                : { measurementUnit: unit },
            })
          : null;

        return (
          <div className="d-flex flex-column gap-1">
            <span className="fw-semibold">
              {value.quantity} {label}
            </span>
            {boxInfo && (
              <>
                <span className="badge bg-primary bg-opacity-15 text-primary
                                 rounded-pill px-2 py-1 fs-6">
                  {boxInfo.boxLabel}
                </span>
                <span className="badge bg-info bg-opacity-15 text-info
                                 rounded-pill px-2 py-1 fs-6">
                  {boxInfo.sizeLabel}
                </span>
              </>
            )}
          </div>
        );
      },
    },
    {
      Header: 'Selling Price',
      accessor: 'convertedTo.sellingPrice',
      Cell: ({ value }) => value != null
        ? `${value.toLocaleString()} birr` : "—",
    },
    {
      Header: 'Batch Value',
      id: 'batchValue',
      accessor: row => row.convertedTo,
      Cell: ({ value }) => {
        if (!value?.quantity || !value?.sellingPrice) return "—";
        const total = value.quantity * value.sellingPrice;
        return (
          <span className="text-success fw-semibold">
            {total.toLocaleString()} birr
          </span>
        );
      },
    },
    {
      Header: 'Reason',
      accessor: 'reason',
      Cell: ({ value }) => dash(value),
    },
    {
      Header: 'Converted By',
      accessor: 'adjustedBy.firstname',
      Filter: SelectColumnFilter, filter: 'multiSelectFilter',
      Cell: ({ value }) => dash(value),
    },
  ], []);

  const data        = useMemo(() =>
    conversions.map(({ convertedTo, ...rest }) => ({
      ...rest,
      convertedTo: convertedTo || null, // keep as single field not spread
    })),
  [conversions]);

  const filterTypes = useMemo(() => ({ multiSelectFilter }), []);

  const tableInstance = useTable({
    columns,
    data,
    filterTypes,
    defaultColumn: { Filter: () => null },
  }, useFilters, useGlobalFilter, usePagination);

  const {
    getTableProps, getTableBodyProps, headerGroups, page,
    nextPage, previousPage, canNextPage, canPreviousPage,
    gotoPage, pageCount, setGlobalFilter, pageOptions,
    setPageSize, state, prepareRow,
    allColumns, getToggleHideAllColumnsProps,
  } = tableInstance;

  const { pageIndex, globalFilter, pageSize } = state;

  if (conversions.length === 0) {
    return (
      <div className="px-4 py-3">
        <p className="text-muted fs-4 text-center py-4">
          No stock conversions recorded yet.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Search + column toggle */}
      <div className="px-4 mb-3">
        <div className="d-flex align-items-center justify-content-between
                        gap-3 px-4 rounded-4 border"
          style={{ backgroundColor: "#faf8f5" }}>
          <GlobalFilter filter={globalFilter} setFilter={setGlobalFilter} />
          <ColumnToggle
            allColumns={allColumns}
            getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
          />
        </div>
      </div>

      {/* Table */}
      <div className="px-4">
        <div className="table-responsive rounded-4 border"
          style={{ backgroundColor: "#ffffff" }}>
          <table className="table table-hover table-striped fs-4"
            {...getTableProps()}
            style={{ minWidth: "100%", margin: 0 }}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th className="px-4"
                      style={{ backgroundColor: "#f0fff4" }}
                      {...column.getHeaderProps()}>
                      {column.render('Header')}
                      {column.canFilter && column.render('Filter')}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {page.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => (
                      <td className="px-4"
                        style={{ backgroundColor: "#ffffff" }}
                        {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="mt-3">
        <ul className="pagination justify-content-center pagination-lg">
          <span style={{ padding: "5px", fontSize: "15px" }}>
            page{" "}
            <strong>
              <em style={{ color: "blue" }}>{pageIndex + 1}</em>{" "}
              of {pageOptions.length}
            </strong>
          </span>
          <select className="form-select form-select-lg"
            style={{ maxWidth: "90px" }}
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}>
            {[10, 20, 30, 50].map((s, i) => (
              <option key={i} value={s}>Show {s}</option>
            ))}
          </select>
          <li>
            <button className="page-link"
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}>{'<<'}</button>
          </li>
          <li className={`page-item ${!canPreviousPage ? "disabled" : ""}`}>
            <button className="page-link"
              onClick={previousPage}
              disabled={!canPreviousPage}>Previous</button>
          </li>
          <li className={`page-item ${!canNextPage ? "disabled" : ""}`}>
            <button className="page-link"
              onClick={nextPage}
              disabled={!canNextPage}>Next</button>
          </li>
          <li>
            <button className="page-link"
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}>{'>>'}</button>
          </li>
        </ul>
      </div>
    </div>
  );
};